/**
 * Passive session buffer for the manual ROS bug reporter.
 * Collects console + /api traffic for attachment when the user chooses to report.
 * Does not auto-queue failures or open UI — reporting is manual only.
 */

const MAX_TRAFFIC = 50
const MAX_CONSOLE = 50
const MAX_MESSAGE = 500
const MAX_BODY = 800
const MAX_DUMP = 24000

const SKIP_URL_PARTS = [
  "/api/health",
  "/api/helpdesk",
]

const traffic = []
const consoleLines = []
let installed = false

function clip(value, max) {
  const text = String(value || "")
  return text.length > max ? `${text.slice(0, max)}\n…` : text
}

function redact(text) {
  return String(text || "")
    .replace(/(authorization|cookie|set-cookie|x-ros-exchange-secret)\s*[:=]\s*\S+/gi, "$1: [redacted]")
    .replace(/bearer\s+[A-Za-z0-9._\-+=/]+/gi, "Bearer [redacted]")
    .replace(/eyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9._\-]+/g, "[redacted-jwt]")
}

function pushCapped(buf, item, max) {
  buf.push(item)
  if (buf.length > max) buf.splice(0, buf.length - max)
}

export function shouldSkipCapture(url) {
  const href = String(url || "")
  return SKIP_URL_PARTS.some((part) => href.includes(part))
}

/** ITMS outages must not mark ROS itself as down. */
export function isHelpdeskApiUrl(url) {
  return String(url || "").includes("/api/helpdesk")
}

/** @deprecated Auto-queue removed — manual reporting only. Kept as no-ops for callers. */
export function getEvents() {
  return []
}

export function getVisibleCount() {
  return 0
}

export function subscribeIncidentEvents(fn) {
  if (typeof fn === "function") fn([])
  return () => {}
}

export function clearIncidentEvents() {}

export function discardIncidentEvent() {
  return false
}

function stringifyArg(value) {
  if (value instanceof Error) return `${value.name}: ${value.message}`
  if (typeof value === "string") return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export function recordConsole(level, args) {
  const message = redact(clip((args || []).map(stringifyArg).join(" "), MAX_MESSAGE))
  if (!message) return
  if (message.includes("ResizeObserver")) return
  pushCapped(consoleLines, {
    t: new Date().toISOString(),
    level,
    message,
  }, MAX_CONSOLE)
}

export function recordApiCall(url, res, method) {
  if (shouldSkipCapture(url)) return
  const status = res && typeof res.status === "number" ? res.status : 0
  // Only keep failures / network errors — never 2xx success codes.
  if (status >= 200 && status < 300) return
  pushCapped(traffic, {
    t: new Date().toISOString(),
    method: (method || "GET").toUpperCase(),
    url: clip(String(url || ""), 400),
    status,
  }, MAX_TRAFFIC)
}

/** No-op: automatic error queueing is disabled. */
export function captureEvent() {
  return undefined
}

/** No-op: automatic API failure queueing is disabled. */
export function captureApiFailure() {}

function sessionSnapshot() {
  if (typeof window === "undefined") return []
  const nav = typeof navigator !== "undefined" ? navigator : {}
  return [
    `Collected at: ${new Date().toISOString()}`,
    `Route: ${window.location.href}`,
    `Referrer: ${document.referrer || "(none)"}`,
    `Viewport: ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio || 1}`,
    `Online: ${nav.onLine !== false}`,
    `Language: ${nav.language || ""}`,
    `Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone || ""}`,
    `User-Agent: ${nav.userAgent || ""}`,
  ]
}

export function formatDiagnostics(extra) {
  const lines = ["ROS incident log", ...sessionSnapshot(), ""]
  if (extra) {
    lines.push("Current crash", extra, "")
  }

  if (traffic.length) {
    lines.push(`Recent API failures (last ${traffic.length}, excl. 2xx):`)
    traffic.forEach((row) => {
      lines.push(`${row.t} ${row.method} ${row.status} ${row.url}`)
    })
    lines.push("")
  } else {
    lines.push("Recent API failures: none.", "")
  }

  if (consoleLines.length) {
    lines.push(`Recent console (${consoleLines.length}):`)
    consoleLines.forEach((row) => {
      lines.push(`${row.t} [${row.level}] ${row.message}`)
    })
  } else {
    lines.push("Recent console: none yet.")
  }

  return clip(lines.join("\n"), MAX_DUMP)
}

export function latestTitle() {
  const path = typeof window !== "undefined" ? window.location.pathname : ""
  return path && path !== "/" ? `ROS issue on ${path}` : "ROS issue"
}

export function installErrorCollector() {
  if (installed || typeof window === "undefined") return
  installed = true

  // Passive console buffer only — no window.error / rejection queueing,
  // and no auto-open of the bug reporter.
  ;["error", "warn", "log", "info"].forEach((level) => {
    const original = console[level].bind(console)
    console[level] = (...args) => {
      original(...args)
      recordConsole(level, args)
    }
  })
}
